import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import DataGrid, {
  Column, FilterRow, HeaderFilter, SearchPanel, Export, Paging, Pager,
  Grouping, GroupPanel, Summary, TotalItem, GroupItem, Toolbar, Item as ToolbarItem
} from "devextreme-react/data-grid";
import { Button } from 'devextreme-react/button';
import DateBox from "devextreme-react/date-box";
import SelectBox from "devextreme-react/select-box";
import TextBox from "devextreme-react/text-box";
import notify from 'devextreme/ui/notify';

import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from "exceljs";
import { saveAs } from 'file-saver';
import API_URL from '../config';

const WAFER_BIN_API = `${API_URL}/api/oracle/soft-bin-report-tot`; 
const CUST_API_URL = `${API_URL}/api/oracle/cust`;
const DEVICE_API_URL = `${API_URL}/api/oracle/device`;
const operList = ['ALL', 'PTST', 'PTST2', 'PTST3', 'PTST4'];
const sbinCountList = [50, 100, 200, 300, 400, 500, 1000];

const styles = `
.loading-overlay { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background-color: rgba(255, 255, 255, 0.7); display: flex; flex-direction: column; justify-content: center; align-items: center; z-index: 10; }
.loading-text { margin-top: 10px; font-size: 16px; color: #333; }
@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
.loading-spinner { border: 4px solid #f3f3f3; border-top: 4px solid #3498db; border-radius: 50%; width: 40px; height: 40px; animation: spin 1s linear infinite; }
`;

export default function WaferBinReport({ user }) {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [rawData, setRawData] = useState([]); 
  const [pivotedData, setPivotedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [comboLoading, setComboLoading] = useState(false);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(firstDayOfMonth);
  const [dateTo, setDateTo] = useState(today);
  const [selectedCust, setSelectedCust] = useState(null);
  
  // [수정 1] 초기값을 'ALL'로 설정 (Device 전체 선택을 기본값으로)
  const [selectedDevice, setSelectedDevice] = useState('ALL'); 
  const [selectedOper, setSelectedOper] = useState(operList[0]);
  const [lotNo, setLotNo] = useState('');
  const [custList, setCustList] = useState([]);
  const [deviceList, setDeviceList] = useState([]);
  const [error, setError] = useState("");
  const [sbinCount, setSbinCount] = useState(100);

  const formatDate = (date) => date ? `${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, "0")}${date.getDate().toString().padStart(2, "0")}` : '';

  useEffect(() => {
    setComboLoading(true);
    axios.get(CUST_API_URL)
      .then(res => {
        if (res.data.retCode === 0) setCustList(res.data.data || []);
        else throw new Error(res.data.retMsg);
      })
      .catch(err => notify(`고객사 목록 로딩 실패: ${err.message}`, 'error', 3000))
      .finally(() => setComboLoading(false));
  }, []);

  useEffect(() => {
    if (user && user.role === "CUST" && custList.length > 0) {
      const userCust = custList.find(c => c.CODE_CD.trim().toUpperCase() === user.username.trim().toUpperCase());
      if (userCust) setSelectedCust(userCust.CODE_CD);
    }
  }, [user, custList]);


 // [수정 2] Device 목록에 'ALL' 옵션 추가 (중복 방지 로직 적용)
 useEffect(() => {
  if (!selectedCust) {
    setDeviceList([{ CODE_CD: 'ALL' }]); // 기본적으로 ALL만 있는 리스트
    setSelectedDevice('ALL');
    return;
  }
  setDeviceLoading(true);
  axios.get(DEVICE_API_URL, { params: { custCd: selectedCust } })
    .then(res => {
      if (res.data.retCode === 0) {
        const devices = res.data.data || [];
        
        // [중요 수정] 서버 데이터에 'ALL'이 포함되어 있을 경우 제거 (중복 방지)
        const filteredDevices = devices.filter(d => d.CODE_CD !== 'ALL');

        // 필터링된 데이터 앞에 'ALL' 수동 추가
        setDeviceList([{ CODE_CD: 'ALL' }, ...filteredDevices]);
        
        setSelectedDevice('ALL'); // 목록 갱신 시 기본값 ALL 선택
      } else {
        throw new Error(res.data.retMsg);
      }
    })
    .catch(err => notify(`디바이스 목록 로딩 실패: ${err.message}`, 'error', 3000))
    .finally(() => setDeviceLoading(false));
}, [selectedCust]);

  const fetchData = useCallback(async () => {
    if (!selectedCust) {
      notify("Customer는 필수 선택 항목입니다.", "warning", 2000);
      return;
    }
    setLoading(true);
    setError("");
    setRawData([]);
    try {
      const params = {
        custCd: selectedCust,
        deviCd: selectedDevice || 'ALL', // 선택된 값이 없으면 'ALL'로 처리
        operCd: selectedOper,
        lotNo: lotNo.trim() || 'ALL',
        dateFrom: formatDate(dateFrom),
        dateTo: formatDate(dateTo),
      };
      const response = await axios.get(WAFER_BIN_API, { params });
      
      const responseData = response.data || { data: [], retMsg: '서버로부터 응답이 없습니다.' };
      if (responseData.retCode !== 0) throw new Error(responseData.retMsg);
      
      const data = responseData.data || [];
      if (data.length === 0) {
        notify("조회된 데이터가 없습니다.", "info", 2000);
      }
      setRawData(data);

    } catch (error) {
      setError(`데이터 조회 실패: ${error.message}`);
      notify(`데이터 조회 실패: ${error.message}`, "error", 3000);
    } finally {
      setLoading(false);
    }
  }, [selectedCust, selectedDevice, selectedOper, lotNo, dateFrom, dateTo]);

  useEffect(() => {
    if (!rawData || rawData.length === 0) {
      setPivotedData([]);
      return;
    }
    const summaryData = rawData.filter(item => (item.data_type || item.DATA_TYPE) === 'SUMMARY');
    const detailsData = rawData.filter(item => (item.data_type || item.DATA_TYPE) === 'DETAILS');

    const detailsMap = new Map();
    for (const detail of detailsData) {
        const lotNoKey = detail.lot_no ? String(detail.lot_no).trim() : '';
        const wafrNoKey = detail.wafr_no ? String(detail.wafr_no).trim() : '';
        const operCdKey = detail.oper_cd ? String(detail.oper_cd).trim() : '';
        const key = `${lotNoKey}|${wafrNoKey}|${operCdKey}`;
        
        if (!detailsMap.has(key)) {
            detailsMap.set(key, new Map());
        }
        detailsMap.get(key).set(detail.sbin, detail.bin_qty);
    }

    const finalData = summaryData.map((summaryRow, index) => {
        const lotNoKey = summaryRow.lot_no ? String(summaryRow.lot_no).trim() : '';
        const wafrNoKey = summaryRow.wafr_no ? String(summaryRow.wafr_no).trim() : '';
        const operCdKey = summaryRow.oper_cd ? String(summaryRow.oper_cd).trim() : '';
        const key = `${lotNoKey}|${wafrNoKey}|${operCdKey}`;

        const newRow = { ...summaryRow, gridId: `${key}_${index}` };
        const waferBins = detailsMap.get(key);

        if (waferBins) {
            for (const [sbin, bin_qty] of waferBins.entries()) {
                if (sbin && !isNaN(sbin)) {
                    newRow[`sbin_${sbin}`] = bin_qty;
                }
            }
        }
        return newRow;
    });
    setPivotedData(finalData);
  }, [rawData]);

  const onExporting = useCallback((e) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Wafer_Bin_Report");
    exportDataGrid({ component: e.component, worksheet, autoFilterEnabled: true })
      .then(() => workbook.xlsx.writeBuffer())
      .then(buffer => saveAs(new Blob([buffer], { type: "application/octet-stream" }), "Wafer_Bin_Report.xlsx"));
    e.cancel = true;
  }, []);

  const columns = useMemo(() => {
    const fixedColumns = [
      { dataField: "cust_cd", caption: "CUSTOMER", width: 120 },
      { dataField: "devi_cd", caption: "MAT_ID", width: 150 },
      { dataField: "oper_cd", caption: "OPER", width: 100 },
      { dataField: "lot_no", caption: "LOT_ID", width: 120 },
      { dataField: "wafr_no", caption: "WAFR_NO", width: 80 },
      { 
        dataField: "strt_tm", 
        caption: "START_TIME", 
        width: 170,
        dataType: "datetime", // 'date'에서 'datetime'으로 변경
        format: "yyyy-MM-dd HH:mm:ss" 
      }, 
      { 
        dataField: "endd_tm", 
        caption: "END_TIME", 
        width: 170,
        dataType: "datetime", // 'date'에서 'datetime'으로 변경
        format: "yyyy-MM-dd HH:mm:ss"
      },
      { dataField: "in_qty", caption: "IN_QTY", dataType: "number", alignment: "right", format: "thousands" },
      { dataField: "out_qty", caption: "OUT_QTY", dataType: "number", alignment: "right", format: "thousands" },
    ];

    const binColumns = Array.from({ length: sbinCount }, (_, i) => {
        const binNum = i + 1;
        const colProps = {
            dataField: `sbin_${binNum}`,
            caption: `SB${binNum}`,
            dataType: "number",
            alignment: "right",
            width: 80,
            cellRender: ({ value }) => (value != null && value > 0 ? value : null)
        };
        return <Column key={colProps.dataField} {...colProps} />;
    });

    return (
      <>
        <Column caption="Lot Summary" isBand={true} fixed={true}>
          {fixedColumns.map(col => <Column key={col.dataField} {...col} />)}
        </Column>
        <Column caption="Bin Details" isBand={true}>
          {binColumns}
        </Column>
      </>
    );
  }, [sbinCount]);

  return (
    <div style={{ padding: 20, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <style>{styles}</style>
      <h2 style={{ marginBottom: '20px' }}>SBIN REPORT TOTAL</h2>
      
      <div style={{ display: "flex", gap: 15, flexWrap: "wrap", alignItems: 'center', marginBottom: '20px' }}>
        <DateBox value={dateFrom} onValueChanged={e => setDateFrom(e.value)} label="From" width={180} type="date" displayFormat="yyyy-MM-dd" />
        <DateBox value={dateTo} onValueChanged={e => setDateTo(e.value)} label="To" width={180} type="date" displayFormat="yyyy-MM-dd" />
        <SelectBox dataSource={custList} value={selectedCust} valueExpr="CODE_CD" displayExpr="CODE_CD" onValueChanged={e => setSelectedCust(e.value)} searchEnabled label="Customer" width={200} disabled={comboLoading || user?.role === "CUST"} showClearButton={user?.role !== "CUST"} placeholder="Select Customer" />
        <SelectBox 
            dataSource={deviceList} 
            value={selectedDevice} 
            valueExpr="CODE_CD" 
            displayExpr="CODE_CD" 
            onValueChanged={e => setSelectedDevice(e.value)} 
            searchEnabled 
            label="Device" 
            width={220} 
            placeholder="Select Device" 
            disabled={deviceLoading || !selectedCust} 
            showClearButton // 사용자가 선택을 해제할 수 있도록 clear 버튼은 유지
        />
        <SelectBox dataSource={operList} value={selectedOper} onValueChanged={e => setSelectedOper(e.value)} label="Operation" width={150} />
        <TextBox value={lotNo} onValueChanged={e => setLotNo(e.value)} placeholder="Enter Lot No." label="Lot No" width={180} />
        <SelectBox
            dataSource={sbinCountList}
            value={sbinCount}
            onValueChanged={e => setSbinCount(e.value)}
            label="SBIN Count"
            width={150}
        />
        <Button text="Search" type="default" icon="search" onClick={fetchData} disabled={loading} />
      </div>

      {error && <p style={{ color: "red", fontWeight: "bold" }}>{error}</p>}

      <div style={{ flex: 1, position: 'relative' }}>
        {loading && <div className="loading-overlay"><div className="loading-spinner"></div><div className="loading-text">Loading...</div></div>}
        <DataGrid
          dataSource={pivotedData}
          keyExpr="gridId"
          showBorders={true}
          columnAutoWidth={false}
          height="100%"
          onExporting={onExporting}
          allowColumnResizing={true}
          hoverStateEnabled={true}
          wordWrapEnabled={true}
          scrolling={{ mode: 'standard', columnRenderingMode: 'virtual' }}
        >
          {columns}

          <Paging defaultPageSize={5} />
          <Pager 
            showPageSizeSelector={true} 
            allowedPageSizes={[5,10, 15, 20, 25, 30]} 
            showInfo={true} 
          />
          
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <Export enabled={true} />
          <GroupPanel visible={true} text="Drag a column header here to group by that column" />
          <Grouping autoExpandAll={false} />
          <SearchPanel visible={true} width={240} placeholder="전체 검색..." />
          <Toolbar>
            <ToolbarItem name="groupPanel" />
            <ToolbarItem location="after" name="exportButton" />
            <ToolbarItem location="after" name="searchPanel" />
          </Toolbar>
          <Summary>
            <TotalItem column="lot_no" summaryType="count" displayFormat="Total Rows: {0}" />
            <TotalItem column="in_qty" summaryType="sum" valueFormat="thousands" displayFormat="Sum: {0}" />
            <TotalItem column="out_qty" summaryType="sum" valueFormat="thousands" displayFormat="Sum: {0}" />
            <GroupItem column="lot_no" summaryType="count" displayFormat="{0} items" />
            <GroupItem column="in_qty" summaryType="sum" valueFormat="thousands" displayFormat="Sum: {0}" showInGroupFooter={true} />
            <GroupItem column="out_qty" summaryType="sum" valueFormat="thousands" displayFormat="Sum: {0}" showInGroupFooter={true} />
          </Summary>
        </DataGrid>
      </div>
    </div>
  );
}